import { computed, Ref, ref, watch, onMounted } from '@vue/composition-api';
import { AcoFormData, AdgroupFormData, FullNormalFormData, PixelType, PlatformType } from '@tt4b/ttam-enums';
import { usePixelInfoApi } from '@tt4b/ttam-hooks';

import { i18n } from '@tt4b/ttam-i18n';
import { useAppList } from './useAppList';
import { useOfflineEventsList } from './useOfflineEventsList';
import AppleIcon from '@tt4b/ttam-assets/src/svgs/apple.svg?url';
import AndroidIcon from '@tt4b/ttam-assets/src/svgs/android.svg?url';

export function useFousTrackTiktokEvent(opt: {
  creativeData: Ref<FullNormalFormData & AcoFormData>;
  isShowWebsiteEvents: Ref<boolean>;
  isShowOfflineEvents: Ref<boolean>;
  isShowAppEvents: Ref<boolean>;
  isRetargetingApp: Ref<boolean>;
}) {
  const { creativeData, isShowWebsiteEvents, isShowOfflineEvents, isShowAppEvents, isRetargetingApp } = opt;

  const pixelName = ref(creativeData.value?.trackingPixelId);
  watch(
    () => creativeData.value.trackingPixelId,
    async (pixelId, oldPixelId) => {
      if (pixelId === oldPixelId) {
        return;
      }
      if (!pixelId) {
        pixelName.value = pixelId;
        return;
      }
      const pixel = await usePixelInfoApi({ pixelId, promotionWebsiteType: PixelType.EXTERNAL_WEBSITE });
      pixelName.value = pixel.name;
    },
    {
      immediate: true,
    },
  );

  const { oriAppList, loadAppList } = useAppList();
  onMounted(() => {
    loadAppList({ is_retargeting: isRetargetingApp.value }, true);
  });
  const app = computed<{ name: string; icon: string }>(() => {
    const appId = creativeData.value?.trackingAppId;
    const selectApp = oriAppList.value.find(item => String(item.Id) === String(appId));
    if (!selectApp) {
      return {
        name: appId === '0' ? '' : appId,
        icon: '',
      };
    }
    return {
      name: selectApp.AppName,
      icon: (selectApp.Platform === PlatformType.ANDROID ? AndroidIcon : AppleIcon) as string,
    };
  });

  const { oriOfflineEventsList } = useOfflineEventsList();
  const offlineName = computed(() => {
    const selectList = (creativeData.value?.trackingOfflineEventSetIds || []) as string[];
    const selectListDeatil = oriOfflineEventsList.value.filter(item => selectList.includes(item.id));
    return selectListDeatil.map(item => item.name).join(', ');
  });

  const options = computed(() => {
    const list = [];

    // 1. pixel
    if (isShowWebsiteEvents.value) {
      list.push({
        label: i18n.t('creative_website_events'),
        value: pixelName.value,
      });
    }

    // 2. app
    if (isShowAppEvents.value) {
      list.push({
        label: i18n.t('creative_tracking_app_events'),
        value: app.value.name,
        icon: app.value?.icon,
      });
    }

    // 3. offonline
    if (isShowOfflineEvents.value) {
      list.push({
        label: i18n.t('module_creative_track_offline_events'),
        value: offlineName.value,
      });
    }

    return list;
  });

  return {
    options,
  };
}
